// src/BadgesPage.js
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, CheckCircle, Trophy, Award, Zap, Target, Swords } from 'lucide-react';
import { auth, firestore } from './firebase';
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { useChallengeStorage } from './hooks/useChallengeStorage';
import Sidebar from './Sidebar';
import './BadgesPage.css';

// ─── Badge definitions ────────────────────────────────────────────────────────
const ALL_BADGES = [
  // Completion
  { id: 'first_blood',   category: 'completion', tier: 'bronze',   icon: '🎯', name: 'First Blood',     description: 'Complete your first challenge',        condition: m => m.totalCompleted >= 1 },
  { id: 'ten_done',      category: 'completion', tier: 'bronze',   icon: '🔟', name: 'Ten Done',         description: 'Complete 10 challenges',               condition: m => m.totalCompleted >= 10 },
  { id: 'fifty_done',    category: 'completion', tier: 'silver',   icon: '🏅', name: 'Half Century',     description: 'Complete 50 challenges',               condition: m => m.totalCompleted >= 50 },
  { id: 'century',       category: 'completion', tier: 'gold',     icon: '💯', name: 'Century',          description: 'Complete 100 challenges',              condition: m => m.totalCompleted >= 100 },
  // Score
  { id: 'sharp_eye',     category: 'score',      tier: 'bronze',   icon: '👁', name: 'Sharp Eye',        description: 'Score 80+ on any challenge',           condition: m => m.bestScore >= 80 },
  { id: 'perfectionist', category: 'score',      tier: 'gold',     icon: '✨', name: 'Perfectionist',    description: 'Score 100 on any challenge',           condition: m => m.perfectScores >= 1 },
  { id: 'high_roller',   category: 'score',      tier: 'silver',   icon: '🎲', name: 'High Roller',      description: 'Get 10 scores above 90',               condition: m => m.highScores >= 10 },
  { id: 'consistent',    category: 'score',      tier: 'silver',   icon: '📊', name: 'Consistent',       description: 'Average score above 75',               condition: m => m.averageScore >= 75 },
  // Streak
  { id: 'on_fire',       category: 'streak',     tier: 'bronze',   icon: '🔥', name: 'On Fire',          description: 'Win 3 challenges in a row',            condition: m => m.bestStreak >= 3 },
  { id: 'unstoppable',   category: 'streak',     tier: 'gold',     icon: '⚡', name: 'Unstoppable',      description: 'Win 10 challenges in a row',           condition: m => m.bestStreak >= 10 },
  { id: 'daily_grind',   category: 'streak',     tier: 'silver',   icon: '📅', name: 'Daily Grind',      description: 'Solve challenges 7 days in a row',     condition: m => m.consecutiveDays >= 7 },
  // Battle
  { id: 'battle_born',   category: 'battle',     tier: 'bronze',   icon: '⚔️', name: 'Battle Born',      description: 'Win your first 1v1 battle',            condition: m => m.battleWins >= 1 },
  { id: 'duelist',       category: 'battle',     tier: 'silver',   icon: '🤺', name: 'Duelist',          description: 'Win 5 battles',                        condition: m => m.battleWins >= 5 },
  { id: 'warlord',       category: 'battle',     tier: 'gold',     icon: '👑', name: 'Warlord',          description: 'Win 25 battles',                       condition: m => m.battleWins >= 25 },
  { id: 'legend',        category: 'battle',     tier: 'platinum', icon: '🏆', name: 'Legend',           description: 'Win 100 battles',                      condition: m => m.battleWins >= 100 },
  { id: 'elo_climb',     category: 'battle',     tier: 'silver',   icon: '📈', name: 'ELO Climber',      description: 'Reach 1200 ELO',                       condition: m => m.currentElo >= 1200 },
  { id: 'prompt_master', category: 'battle',     tier: 'gold',     icon: '🔮', name: 'Prompt Master',    description: 'Reach 1800 ELO',                       condition: m => m.currentElo >= 1800 },
  { id: 'comeback_kid',  category: 'battle',     tier: 'silver',   icon: '🔄', name: 'Comeback Kid',     description: 'Win after losing 3 in a row',          condition: m => m.comebackWins >= 1 },
  { id: 'bot_slayer',    category: 'battle',     tier: 'bronze',   icon: '🤖', name: 'Bot Slayer',       description: 'Beat an AI bot opponent',              condition: m => m.botWins >= 1 },
  // Difficulty
  { id: 'hard_mode',     category: 'difficulty', tier: 'silver',   icon: '🔱', name: 'Hard Mode',        description: 'Complete 5 hard challenges',           condition: m => m.hardCompleted >= 5 },
  { id: 'expert_mind',   category: 'difficulty', tier: 'gold',     icon: '🧠', name: 'Expert Mind',      description: 'Complete an expert challenge',         condition: m => m.expertCompleted >= 1 },
  // Special
  { id: 'night_owl',     category: 'special',    tier: 'bronze',   icon: '🦉', name: 'Night Owl',        description: 'Solve 5 challenges after 10 PM',       condition: m => m.nightOwlChallenges >= 5 },
  { id: 'early_bird',    category: 'special',    tier: 'bronze',   icon: '🐦', name: 'Early Bird',       description: 'Solve 5 challenges before 8 AM',       condition: m => m.earlyBirdChallenges >= 5 },
  { id: 'weekend_grind', category: 'special',    tier: 'bronze',   icon: '🏖', name: 'Weekend Grinder',  description: 'Solve challenges on 4 weekends',       condition: m => m.weekendChallenges >= 4 },
  { id: 'first_try',     category: 'special',    tier: 'silver',   icon: '🎯', name: 'First Try',        description: 'Solve 10 challenges on first attempt', condition: m => m.firstTryWins >= 10 },
];

const TIERS = {
  bronze:   { color: '#cd7f32', label: 'Bronze',   icon: '🥉', bg: 'rgba(205,127,50,0.1)',   border: 'rgba(205,127,50,0.25)' },
  silver:   { color: '#9ca3af', label: 'Silver',   icon: '🥈', bg: 'rgba(156,163,175,0.1)', border: 'rgba(156,163,175,0.25)' },
  gold:     { color: '#f59e0b', label: 'Gold',     icon: '🥇', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.25)' },
  platinum: { color: '#a78bfa', label: 'Platinum', icon: '💎', bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.25)' },
};

const CATEGORY_META = {
  all:        { icon: <Award size={14} />,   label: 'All' },
  completion: { icon: <Target size={14} />,  label: 'Completion' },
  score:      { icon: <Zap size={14} />,     label: 'Score' },
  streak:     { icon: '🔥',                  label: 'Streak' },
  battle:     { icon: <Swords size={14} />,  label: 'Battle' },
  difficulty: { icon: '🔱',                  label: 'Difficulty' },
  special:    { icon: '⭐',                   label: 'Special' },
};

// ─── Badge card ───────────────────────────────────────────────────────────────
const BadgeCard = ({ badge }) => {
  const tier = TIERS[badge.tier];
  return (
    <div
      className={`bp-badge ${badge.unlocked ? 'unlocked' : 'locked'}`}
      style={badge.unlocked ? { borderColor: tier.border } : {}}
    >
      {/* Tier dot */}
      <div className="bp-badge-tier-dot" style={{ background: tier.color }} />

      {/* Icon */}
      <div
        className="bp-badge-icon"
        style={badge.unlocked ? { background: tier.bg } : {}}
      >
        {badge.unlocked ? badge.icon : <Lock size={20} />}
      </div>

      {/* Info */}
      <div className="bp-badge-name">{badge.name}</div>
      <div className="bp-badge-desc">{badge.description}</div>

      {/* Status */}
      {badge.unlocked ? (
        <div className="bp-badge-status unlocked">
          <CheckCircle size={11} /> Unlocked
        </div>
      ) : (
        <div className="bp-badge-status locked">
          {tier.icon} {tier.label}
        </div>
      )}
    </div>
  );
};

// ─── Progress ring ────────────────────────────────────────────────────────────
const Ring = ({ value, max, color = '#9D00FF', size = 100, stroke = 8 }) => {
  const r   = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct  = Math.min(1, value / max);
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--bp-border)" strokeWidth={stroke} />
      <circle
        cx={size/2} cy={size/2} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={`${pct * circ} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dasharray 0.7s ease' }}
      />
    </svg>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const BadgesPage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeCat, setActiveCat]     = useState('all');
  const [tierFilter, setTierFilter]   = useState('all');

  const { completedChallenges, loading } = useChallengeStorage();

  const user  = auth.currentUser;
  const myUid = user?.uid;

  // ── Compute metrics from real data ────────────────────────────────────────
  const metrics = useMemo(() => {
    const scores = completedChallenges.map(c => c.score).filter(Boolean);
    const avg    = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

    let nightOwlChallenges = 0, earlyBirdChallenges = 0;
    const weekendDates = new Set();

    completedChallenges.forEach(c => {
      if (!c.completedAt) return;
      const d   = new Date(c.completedAt?.seconds ? c.completedAt.seconds * 1000 : c.completedAt);
      const h   = d.getHours();
      const day = d.getDay();
      if (h >= 22 || h < 4)  nightOwlChallenges++;
      if (h >= 5  && h < 8)  earlyBirdChallenges++;
      if (day === 0 || day === 6) weekendDates.add(d.toDateString());
    });

    // Streak
    const uniqueDays = [...new Set(
      completedChallenges
        .filter(c => c.completedAt)
        .map(c => {
          const d = new Date(c.completedAt?.seconds ? c.completedAt.seconds * 1000 : c.completedAt);
          return d.toDateString();
        })
    )].sort((a, b) => new Date(a) - new Date(b));

    let bestStreak = 0, currentStreak = 0, consecutiveDays = 0;
    for (let i = 0; i < uniqueDays.length; i++) {
      if (i === 0) { currentStreak = 1; }
      else {
        const diff = (new Date(uniqueDays[i]) - new Date(uniqueDays[i - 1])) / 86400000;
        currentStreak = diff === 1 ? currentStreak + 1 : 1;
      }
      if (currentStreak > bestStreak) bestStreak = currentStreak;
    }
    if (uniqueDays.length > 0) {
      const diffDays = Math.floor((Date.now() - new Date(uniqueDays[uniqueDays.length - 1])) / 86400000);
      consecutiveDays = diffDays <= 1 ? currentStreak : 0;
    }

    return {
      totalCompleted:      completedChallenges.length,
      bestScore:           scores.length ? Math.max(...scores) : 0,
      averageScore:        avg,
      perfectScores:       completedChallenges.filter(c => c.score === 100).length,
      highScores:          completedChallenges.filter(c => c.score >= 90).length,
      bestStreak,
      currentStreak,
      consecutiveDays,
      hardCompleted:       completedChallenges.filter(c => ['hard','Hard'].includes(c.difficulty)).length,
      expertCompleted:     completedChallenges.filter(c => ['expert','Expert'].includes(c.difficulty)).length,
      nightOwlChallenges,
      earlyBirdChallenges,
      weekendChallenges:   weekendDates.size,
      firstTryWins:        completedChallenges.filter(c => c.attempts === 1 && c.status === 'solved').length,
      // Battle metrics — would need Firestore data; defaulting here
      battleWins:          0,
      botWins:             0,
      comebackWins:        0,
      currentElo:          1000,
    };
  }, [completedChallenges]);

  // ── Evaluated badges ──────────────────────────────────────────────────────
  const evaluated = useMemo(
    () => ALL_BADGES.map(b => ({ ...b, unlocked: b.condition(metrics) })),
    [metrics]
  );

  // ── Filter ────────────────────────────────────────────────────────────────
  const displayed = useMemo(() => {
    let list = activeCat === 'all' ? evaluated : evaluated.filter(b => b.category === activeCat);
    if (tierFilter !== 'all') list = list.filter(b => b.tier === tierFilter);
    // Unlocked first
    return [...list].sort((a, b) => (b.unlocked ? 1 : 0) - (a.unlocked ? 1 : 0));
  }, [evaluated, activeCat, tierFilter]);

  // ── Summary numbers ───────────────────────────────────────────────────────
  const unlocked = evaluated.filter(b => b.unlocked).length;
  const total    = evaluated.length;

  const tierCounts = useMemo(() =>
    Object.keys(TIERS).reduce((acc, t) => {
      acc[t] = { total: evaluated.filter(b => b.tier === t).length, unlocked: evaluated.filter(b => b.tier === t && b.unlocked).length };
      return acc;
    }, {}),
    [evaluated]
  );

  return (
    <div className="bp-shell">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      {sidebarOpen && <div className="bp-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className={`bp-main ${sidebarOpen ? 'sidebar-open' : ''}`}>

        <button className="bp-hamburger" onClick={() => setSidebarOpen(v => !v)}>
          <span /><span /><span />
        </button>

        {/* ── Header ── */}
        <div className="bp-header">
          <div className="bp-header-left">
            <div className="bp-header-icon"><Award size={18} /></div>
            <div>
              <h1>Badges</h1>
              <p className="bp-header-sub">{unlocked} of {total} unlocked</p>
            </div>
          </div>
          <button className="bp-cta" onClick={() => navigate('/challenges')}>
            <Zap size={13} /> Earn more
          </button>
        </div>

        {/* ── Progress strip ── */}
        <div className="bp-progress-strip">
          {/* Big ring */}
          <div className="bp-ring-wrap">
            <Ring value={unlocked} max={total} size={88} stroke={7} />
            <div className="bp-ring-inner">
              <span className="bp-ring-num">{unlocked}</span>
              <span className="bp-ring-denom">/{total}</span>
            </div>
          </div>

          {/* Tier breakdown */}
          {Object.entries(TIERS).map(([key, t]) => (
            <div
              key={key}
              className={`bp-tier-stat ${tierFilter === key ? 'active' : ''}`}
              style={tierFilter === key ? { borderColor: t.border, background: t.bg } : {}}
              onClick={() => setTierFilter(f => f === key ? 'all' : key)}
            >
              <Ring
                value={tierCounts[key].unlocked}
                max={tierCounts[key].total}
                color={t.color}
                size={52}
                stroke={5}
              />
              <div className="bp-tier-stat-inner">
                <span className="bp-tier-stat-num" style={{ color: t.color }}>
                  {tierCounts[key].unlocked}/{tierCounts[key].total}
                </span>
                <span className="bp-tier-stat-label">{t.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Category tabs ── */}
        <div className="bp-cats">
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const catBadges  = key === 'all' ? evaluated : evaluated.filter(b => b.category === key);
            const catUnlocked = catBadges.filter(b => b.unlocked).length;
            return (
              <button
                key={key}
                className={`bp-cat ${activeCat === key ? 'active' : ''}`}
                onClick={() => setActiveCat(key)}
              >
                <span className="bp-cat-icon">{meta.icon}</span>
                <span className="bp-cat-label">{meta.label}</span>
                <span className="bp-cat-count">{catUnlocked}/{catBadges.length}</span>
              </button>
            );
          })}
        </div>

        {/* ── Grid ── */}
        <div className="bp-grid-wrap">
          {loading ? (
            <div className="bp-loading">
              <div className="bp-spinner" />
              <span>Loading badges…</span>
            </div>
          ) : displayed.length === 0 ? (
            <div className="bp-empty">
              <Trophy size={36} />
              <p>No badges in this category yet</p>
            </div>
          ) : (
            <div className="bp-grid">
              {displayed.map(b => <BadgeCard key={b.id} badge={b} />)}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default BadgesPage;